'use client';

import * as React from 'react';
import { Popover as PopoverPrimitive } from 'radix-ui';

import { cn } from '@/lib/utils';

function Popover({ ...props }: React.ComponentProps<typeof PopoverPrimitive.Root>) {
    return <PopoverPrimitive.Root data-slot="popover" {...props} />;
}

function PopoverTrigger({ ...props }: React.ComponentProps<typeof PopoverPrimitive.Trigger>) {
    return <PopoverPrimitive.Trigger data-slot="popover-trigger" {...props} />;
}

function PopoverContent({
    className,
    align = 'center',
    sideOffset = 4,
    onWheel,
    onTouchMove,
    container,
    ...props
}: React.ComponentProps<typeof PopoverPrimitive.Content> &
    Pick<React.ComponentProps<typeof PopoverPrimitive.Portal>, 'container'>) {
    return (
        // `container` is optional and defaults to `<body>`, as before. A popover
        // opened inside a Sheet/Dialog can pass the modal's own element (see
        // `useOverlayHost`) so it sits inside the modal's focus trap and scroll lock.
        <PopoverPrimitive.Portal container={container}>
            <PopoverPrimitive.Content
                data-slot="popover-content"
                align={align}
                sideOffset={sideOffset}
                // A popover opened from inside a Dialog or Sheet is portaled to
                // `<body>`, outside the modal's scroll lock, and the lock cancels
                // every wheel / touch-move that reaches the document from out
                // there — so a list in the popover (the contact picker in "New
                // opportunity") could not be scrolled. Stopping the event here
                // keeps it from reaching the lock; the popover scrolls natively.
                onWheel={(event) => {
                    event.stopPropagation();
                    onWheel?.(event);
                }}
                onTouchMove={(event) => {
                    event.stopPropagation();
                    onTouchMove?.(event);
                }}
                className={cn(
                    'z-50 flex max-h-(--radix-popover-content-available-height) w-72 origin-(--radix-popover-content-transform-origin) flex-col gap-2.5 overflow-x-hidden overflow-y-auto rounded-lg border border-border-default bg-bg-surface p-2.5 text-sm text-text-primary shadow-md outline-hidden duration-100 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95 data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95',
                    className,
                )}
                {...props}
            />
        </PopoverPrimitive.Portal>
    );
}

function PopoverAnchor({ ...props }: React.ComponentProps<typeof PopoverPrimitive.Anchor>) {
    return <PopoverPrimitive.Anchor data-slot="popover-anchor" {...props} />;
}

function PopoverHeader({ className, ...props }: React.ComponentProps<'div'>) {
    return (
        <div
            data-slot="popover-header"
            className={cn('flex flex-col gap-0.5 text-sm', className)}
            {...props}
        />
    );
}

function PopoverTitle({ className, ...props }: React.ComponentProps<'h2'>) {
    return (
        <div
            data-slot="popover-title"
            className={cn('font-medium text-text-heading', className)}
            {...props}
        />
    );
}

function PopoverDescription({ className, ...props }: React.ComponentProps<'p'>) {
    return (
        <p
            data-slot="popover-description"
            className={cn('text-text-secondary', className)}
            {...props}
        />
    );
}

export {
    Popover,
    PopoverAnchor,
    PopoverContent,
    PopoverDescription,
    PopoverHeader,
    PopoverTitle,
    PopoverTrigger,
};
